import { useEffect, useRef } from "react";
import { FlowComponentHeader } from "@/components/ui/flow-component-header";
import {
  useApplyPreviousAnswer,
  useReuseAvailability,
} from "@/lib/hooks/use-flow-instances/use-reuse-previous-answer";
import {
  useIdentityConflicts,
  useSumsubVerificationFlowInstanceValue,
} from "@/lib/hooks/use-flow-instances/use-sumsub-verification";
import type { ComponentStyleType } from "@/lib/types/flow";
import type { ActingRepresentative } from "../representative-note";
import { SumsubVerificationWidget } from "../sumsub-verification/sumsub-verification-widget";
import { WizardSumsubSubject } from "./wizard-sumsub-subject";

/** True once the saved snapshot holds a terminal Sumsub review answer. */
function isTerminalValue(calculatedData: string | null): boolean {
  if (!calculatedData) return false;
  try {
    const ans = JSON.parse(calculatedData)?.status?.reviewAnswer;
    return ans === "GREEN" || ans === "RED";
  } catch {
    return false;
  }
}

type WizardSumsubVerificationProps = {
  instanceId: string;
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  language: string;
  componentId: string;
  disabled?: boolean;
  hasValidationError?: boolean;
  actingRepresentative?: ActingRepresentative | null;
  /**
   * QL-654 — the people to verify when there are two or more, each verifying
   * for themselves. Absent for a single person (the ordinary widget).
   */
  subjects?: Array<{ subjectId: string | null; name: string | null }>;
  /** The people are still loading: render nothing rather than flash the single-person widget. */
  subjectsLoading?: boolean;
};

export function WizardSumsubVerification({
  instanceId,
  style = "none",
  title,
  description,
  language,
  componentId,
  disabled,
  hasValidationError,
  actingRepresentative,
  subjects,
  subjectsLoading,
}: WizardSumsubVerificationProps) {
  const {
    inputData,
    calculatedData,
    isLoading: valueLoading,
    syncFromSumsub,
  } = useSumsubVerificationFlowInstanceValue(instanceId);
  // QL-f — fields where the scanned document disagrees with the client record.
  // Answering them all is what completes the component.
  const conflicts = useIdentityConflicts(instanceId, calculatedData);
  const {
    available,
    sourceDate,
    isLoading: availabilityLoading,
  } = useReuseAvailability(instanceId, "sumsub_verification");
  const applyPrevious = useApplyPreviousAnswer(instanceId, "sumsub_verification");

  const handleRefetchValue = async () => {
    const fresh = await syncFromSumsub();
    return fresh?.calculatedData ?? null;
  };

  // Recover state on open: if there's no terminal result saved locally (e.g. the
  // applicantReviewed webhook never landed), sync once from Sumsub. This restores
  // a completed verification, or surfaces an in-progress one, instead of resetting
  // to "Start Verification". No-op when nothing was ever started (no applicant).
  const didInitialSyncRef = useRef(false);
  useEffect(() => {
    if (valueLoading || didInitialSyncRef.current) return;
    didInitialSyncRef.current = true;
    if (!isTerminalValue(calculatedData)) void syncFromSumsub();
  }, [valueLoading, calculatedData, syncFromSumsub]);

  if (valueLoading || availabilityLoading || subjectsLoading) return null;

  const people = (subjects ?? []).filter((s): s is { subjectId: string; name: string | null } =>
    Boolean(s.subjectId),
  );
  if (people.length >= 2) {
    return (
      <div className="space-y-4">
        <FlowComponentHeader title={title} description={description} />
        {people.map((person) => (
          <WizardSumsubSubject
            key={person.subjectId}
            instanceId={instanceId}
            componentId={componentId}
            subjectId={person.subjectId}
            name={person.name}
            style={style === "none" ? "border" : style}
            language={language}
            calculatedData={calculatedData}
            onRefetchValue={handleRefetchValue}
            disabled={disabled}
            hasValidationError={hasValidationError}
          />
        ))}
      </div>
    );
  }

  return (
    <SumsubVerificationWidget
      componentId={componentId}
      actingRepresentative={actingRepresentative}
      style={style}
      title={title}
      description={description}
      language={language}
      inputData={inputData}
      calculatedData={calculatedData}
      onRefetchValue={handleRefetchValue}
      accessTokenUrl={`/internal/flow-instances/${instanceId}/sumsub-verification/access-token`}
      identityConflicts={conflicts.rows}
      onResolveIdentityConflict={conflicts.resolve}
      disabled={disabled}
      available={available}
      sourceDate={sourceDate}
      onApply={async () => {
        await applyPrevious.mutateAsync();
      }}
      hasValidationError={hasValidationError}
    />
  );
}
